import { DateTime } from 'luxon';
import { AnalyticsByDay, Period } from 'graphql/types';

export enum DropdownLabel {
  supervisorProductivity = 'supervisorProductivity',
  supervisorUtilization = 'supervisorUtilization'
}
export const utilizationColorSet = new Map<string, string>([
  ['Meeting', '#2196f3'],
  ['Scheduling', '#e02e74'],
  ['Messaging', '#4caf50'],
  ['Email', '#ffeb3b'],
  ['Software Development', '#a60acc'],
  ['Project Management', '#230acc'],
  ['Design', '#0accb7'],
  ['IT Service Management', '#cbcc0a'],
  ['Browser', '#c66f08'],
  ['Productivity', '#827717'],
  ['Desktop', '#212121']
]);

export const productivitycolorSet = new Map<string, string>([
  ['work-schedule', '#757575'],
  ['unaccounted-hours', '#93b0d0'],
  ['time-entry-billable-duration', '#26d293'],
  ['time-entry-non-billable-duration', '#d2f9ea']
]);

const defaultColor = '#607d8b';

export type dataType = {
  name: string;
  color: string;
  data: AnalyticsByDay[];
};

const getId = (isUtilizationSelected: boolean, dimensionData: any) => {
  if (!isUtilizationSelected) {
    const i: string = dimensionData.metadata[1].value;
    return i;
  }
  return dimensionData.name;
};

export type UtilizationDataType = {
  categoryName: string;
  data: { date: string; totalCount: number; totalDuration: number }[];
};
export function getDaterange(
  currentPeriod: Period | null,
  checked: boolean,
  data: UtilizationDataType[]
): Set<string> {
  let dateSet = new Set<string>();
  if (!checked) {
    let startDate = DateTime.fromISO(currentPeriod?.startDate);
    const endDate = DateTime.fromISO(currentPeriod?.endDate);

    while (startDate <= endDate) {
      dateSet.add(startDate.toISODate());
      startDate = startDate.plus({ days: 1 });
    }
  } else {
    let dates: string[] = [];
    data.forEach((v) => {
      dates = [...dates, ...v.data.map((x) => x.date)];
    });
    dateSet = new Set(dates);
  }
  return dateSet;
}

export function transformData<T>(
  data: T[],
  isUtilizationSelected: boolean
): UtilizationDataType[] {
  const existingCategoryObj: Record<string, number> = {};
  return data?.reduce((acc: UtilizationDataType[], item: any) => {
    const id: string = getId(isUtilizationSelected, item?.dimension);
    if (id) {
      if (id in existingCategoryObj) {
        const idx = existingCategoryObj[id];
        acc[idx].data.push({
          date: item.dimension.metadata[0].value,
          totalCount: item.metrics.totalCount,
          totalDuration: item.metrics.totalDuration
        });
      } else {
        const v = {
          categoryName: id,
          data: [
            {
              date: item.dimension.metadata[0].value,
              totalCount: item.metrics.totalCount,
              totalDuration: item.metrics.totalDuration
            }
          ]
        };
        acc.push(v);
        existingCategoryObj[id] = acc.length - 1;
      }
    }

    return acc;
  }, []);
}

export function transformUtilzationAnalyticsData(
  data: UtilizationDataType[],
  checked: boolean,
  currentPeriod: Period | null,
  colorSet?: Map<string, string>
): dataType[] {
  let dateSet: Set<string>;
  dateSet = getDaterange(currentPeriod, checked, data);

  const transformedData = data.map((d) => {
    if (checked) {
      dateSet = getDaterange(currentPeriod, checked, data);
    }
    const color =
      (colorSet || utilizationColorSet).get(d.categoryName) || defaultColor;

    const existingData = [...d.data];
    const presentDates = d.data.map((x) => x.date);
    dateSet.forEach((z) => {
      if (!presentDates.includes(z)) {
        existingData.push({
          date: z,
          totalCount: 0,
          totalDuration: 0
        });
      }
    });
    existingData.sort((a, b) => {
      if (a.date < b.date) {
        return -1;
      }
      if (a.date > b.date) {
        return 1;
      }
      return 0;
    });

    return {
      name: d.categoryName,
      color: color,
      data: existingData
    };
  });

  transformedData.sort((a, b) => a.name.localeCompare(b.name));

  return transformedData;
}

export function transformIntoUtilizationChartData<T>(
  data: T[] | undefined,
  checked: boolean,
  currentPeriod: Period | null,
  isUtilizationSelected: boolean,
  colorSet?: Map<string, string>
): dataType[] {
  let filteredData = data;
  if (checked)
    filteredData = data?.filter((i: any) => i.metrics.totalDuration !== 0);
  const d = transformData(filteredData || [], isUtilizationSelected);
  const transformD = transformUtilzationAnalyticsData(
    d,
    checked,
    currentPeriod,
    colorSet
  );
  return transformD;
}
